import { useState, useEffect, useRef } from "react";
import { useAuth, api } from "../App";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import PostCard from "../components/PostCard";
import CreatePost from "../components/CreatePost";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const FeedPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  useAuth(); // Ensure user is authenticated
  const _navigate = useNavigate();
  const observerRef = useRef();

  const fetchPosts = async (skip = 0, append = false) => {
    try {
      if (skip === 0) setLoading(true);
      else setLoadingMore(true);

      const response = await api.get(`/posts/feed?skip=${skip}&limit=20`);
      
      if (append) {
        setPosts(prev => [...prev, ...response.data]);
      } else {
        setPosts(response.data);
      }
      
      setHasMore(response.data.length === 20);
    } catch (_error) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchPosts(posts.length, true);
    }
  };

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length, hasMore, loadingMore]);

  const handlePostCreated = (newPost) => {
    setPosts(prev => [newPost, ...prev]);
  };

  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-6 px-4" data-testid="feed-page">
        <h1 className="text-2xl font-bold mb-6">Your Feed</h1>
        
        {/* Create Post */}
        <CreatePost onPostCreated={handlePostCreated} />

        {/* Posts Feed */}
        <div className="space-y-4 mt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="empty-feed">
              <p className="text-lg mb-2">No posts yet</p>
              <p className="text-sm">Be the first to share something!</p>
            </div>
          ) : (
            <>
              {posts.map((post, index) => (
                <div 
                  key={post.id} 
                  className="animate-fade-in"
                  style={{ animationDelay: `${Math.min(index * 0.05, 0.3)}s` }}
                >
                  <PostCard 
                    post={post} 
                    onDelete={handlePostDeleted}
                    onUpdate={handlePostUpdated}
                  />
                </div>
              ))}
              
              {/* Load more trigger */}
              <div ref={observerRef} className="py-4">
                {loadingMore && (
                  <div className="flex justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default FeedPage;
